import { useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  BrowserRouter,
  Routes,
  Route,
  useSearchParams,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { queryClient } from './lib/react-query';
import { initSentry } from './lib/sentry';
import i18n from './lib/i18n';
import { PostHogProvider, useAnalytics } from './features/analytics';
import { useAuthStore } from './features/auth/store/auth-store';
import { useOperatorStore } from './features/operator/store/operator-store';
import { useLocaleStore } from './features/locale/store/locale-store';
import { useUrlParams } from './hooks/useUrlParams';
import { useDeepLinking } from './features/charge-points/hooks/useDeepLinking';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useApplyTheme } from './features/theme/hooks/useTheme';
import { useChargeSessionMessages } from './features/charge-sessions/hooks/useChargeSessionMessages';
import { useChargeSessionMockData } from './features/charge-sessions/dev/mock-charge-sessions';
import { useChargeSessionsStore } from './features/charge-sessions/store/charge-sessions-store';
import { ChargeSessionDetailPanel } from './features/charge-sessions/components/ChargeSessionDetailPanel';
import { ResponsiveLayout } from './components/layout/ResponsiveLayout';
import { StressTest } from './pages/StressTest';
import { DocsPage } from './pages/DocsPage';
import { ReadmePage } from './pages/ReadmePage';

// Initialize Sentry
initSentry();

// Error fallback component
function ErrorFallback({ error }: { error: Error }) {
  return (
    <div className="flex items-center justify-center h-screen bg-monta-gray-50">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-monta-red mb-4">
          Something went wrong
        </h1>
        <p className="text-monta-gray-600 mb-4">{error.message}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-monta-black text-white rounded-full hover:bg-monta-black-600"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

// App initializer component
function AppInitializer({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((state) => state.initialize);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const hasCachedOperator = useOperatorStore((state) => !!state.operator);
  const authError = useAuthStore((state) => state.error);
  const clearError = useAuthStore((state) => state.clearError);
  const initializeOperator = useOperatorStore((state) => state.initialize);
  const isOperatorInitialized = useOperatorStore(
    (state) => state.isInitialized
  );
  const initializeLocale = useLocaleStore((state) => state.initialize);
  const { trackEvent, trackPageView } = useAnalytics();
  const [searchParams] = useSearchParams();
  // Charge-sessions mode renders live session markers fed only by postMessage
  // and must do ZERO charge-point/operator fetching (BIZOPS-1588) — a stray 401
  // there would silently re-mint a guest token. Compute the flag once and use it
  // to skip the charge-point-only initialization paths below.
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';
  useUrlParams(); // Initialize URL params
  useDeepLinking(); // Handle deep linking to charge points (no-op in sessions mode)
  useApplyTheme(); // light/dark class on <html>
  useDocumentTitle(); // Update document title based on operator
  // Charge-session map (BIZOPS-1588): own the postMessage bridge to the Hub
  // studio-viewer parent here so there is exactly one listener / one ready
  // handshake for the whole app.
  const { requestSessionDetail } = useChargeSessionMessages();
  // DEV-ONLY: when `?mock=1` is set alongside `?mapType=chargeSessions` in a dev
  // build, impersonate the Hub parent so the sessions map can be developed with
  // no Hub/backend. No-op in production and in any normal session (see
  // mock-charge-sessions.ts / URL_PARAMETERS.md "Local mock mode").
  useChargeSessionMockData();
  const selectedSessionPointId = useChargeSessionsStore(
    (state) => state.selectedPointId
  );

  // When the user opens a leaf, ask the parent for its Tier-2 detail (the leaf
  // click only records the selection in the store). Only leaves are selectable
  // — clusters zoom in instead — so detail-requests are leaf-only by design.
  useEffect(() => {
    if (!selectedSessionPointId) {
      return;
    }
    const selectedLeaf = useChargeSessionsStore
      .getState()
      .leaves.find((leaf) => leaf.id === selectedSessionPointId);
    if (selectedLeaf) {
      requestSessionDetail(selectedLeaf.id, selectedLeaf.level);
    }
  }, [selectedSessionPointId, requestSessionDetail]);

  useEffect(() => {
    // Initialize locale from saved settings or browser detection
    initializeLocale();

    // Sync i18n with detected locale
    const currentLocale = useLocaleStore.getState().currentLocale;
    void i18n.changeLanguage(currentLocale);

    // Auth (guest token) and operator/theme init run in BOTH modes. Neither
    // blocks rendering any more (API requests wait on the auth gate), so
    // skipping them in sessions mode is now possible — that's BIZOPS-1600.
    void initialize(); // sign up as guest if needed
    void initializeOperator(); // operator + theme (uses Operator header from URL)

    // Zustand selectors are stable, safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cache all operators (for icon display and the charge-point operator
  // filter) once the guest token exists - fetching at mount raced sign-up and
  // 401'd on first visits. Charge-sessions mode has no operator filter and
  // renders only postMessage-fed markers, so skip this charge-point-only fetch
  // there (BIZOPS-1588). Runs regardless of the ?operator= URL parameter.
  useEffect(() => {
    if (isInitialized && !isChargeSessionsMode) {
      void useLocaleStore.getState().initializeOperatorsCache();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  // Track app loaded event after initialization
  useEffect(() => {
    if (isInitialized && isOperatorInitialized) {
      trackEvent('app_loaded', {
        locale: useLocaleStore.getState().currentLocale,
        env: import.meta.env.VITE_ENV,
      });
      trackPageView('Map');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isOperatorInitialized]);

  // Render immediately: auth is awaited per-request (api-client auth gate) so
  // Google Maps loads in parallel. Only hold for a branded map we have no
  // cached theme for, to avoid flashing Monta branding.
  const requestedOperator = searchParams.get('operator') || 'monta';
  if (
    !isOperatorInitialized &&
    requestedOperator !== 'monta' &&
    !hasCachedOperator
  ) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-white">
        {/* Logo */}
        <div className="mb-8">
          <svg
            width="120"
            height="28"
            viewBox="0 0 149 28"
            fill="currentColor"
            className="text-monta-black"
          >
            <g clipPath="url(#clip0_1924_2917)">
              <path
                d="M27.0229 27.8718C26.8948 27.8718 26.7642 27.8417 26.6461 27.7839L13.9359 21.4841L1.2258 27.7839C0.962053 27.9145 0.650579 27.8995 0.401902 27.7437C0.153225 27.588 0 27.3167 0 27.0228V0.848971C0 0.55508 0.153225 0.281285 0.401902 0.125548C0.653091 -0.0276774 0.967076 -0.0427487 1.22831 0.0903812L13.9359 6.48062L26.6436 0.0903812C26.9073 -0.0427487 27.2188 -0.0276774 27.47 0.125548C27.7212 0.278773 27.8719 0.552568 27.8719 0.846459V27.0228C27.8719 27.3167 27.7212 27.588 27.47 27.7437C27.3344 27.8291 27.1786 27.8718 27.0229 27.8718ZM15.845 20.5371L26.1739 25.6564V15.4154L15.845 20.5346V20.5371ZM1.69552 15.4179V25.6589L12.0244 20.5396L1.69552 15.4179ZM2.71033 14.0263L13.9359 19.5902L25.1616 14.0263L13.9359 8.37961L2.71033 14.0263ZM15.8249 7.43011L26.1764 12.6373V2.22548L15.8249 7.43262V7.43011ZM1.69552 2.22548V12.6373L12.047 7.43011L1.69552 2.22548Z"
                fill="currentColor"
              />
              <path
                d="M41.8984 0.562683H49.4818L56.8743 22.283L64.3421 0.562683H71.6969V27.4273H67.0098L67.163 4.63947L59.2757 27.4273H54.3222L46.4349 4.63947L46.5505 27.4273H41.9009V0.562683H41.8984ZM74.5278 17.8997C74.5278 11.9541 78.9111 7.8773 84.9296 7.8773C90.948 7.8773 95.3313 11.9541 95.3313 17.8997C95.3313 23.8454 90.9882 27.9975 84.9296 27.9975C78.8709 27.9975 74.5278 23.9207 74.5278 17.8997ZM84.9296 24.3779C88.4362 24.3779 90.6466 21.6726 90.6466 17.8997C90.6466 14.1269 88.4738 11.4969 84.9698 11.4969C81.4657 11.4969 79.215 14.049 79.215 17.8997C79.215 21.7504 81.4632 24.3779 84.9321 24.3779H84.9296ZM98.0466 8.45001H102.505V10.9644C103.191 9.8215 105.173 7.8773 108.526 7.8773C112.603 7.8773 115.652 10.354 115.652 15.2321V27.4248H111.081V15.84C111.081 12.7152 109.556 11.5723 107.155 11.5723C103.917 11.5723 102.658 14.0867 102.658 17.4777V27.4223H98.0466V8.45001ZM120.224 21.4063V11.7657H116.986V8.45001H120.224V3.64979H124.796V8.45001H129.063V11.7657H124.796V21.331C124.796 23.1596 125.672 23.7323 127.272 23.7323H129.063V27.4298H125.901C122.244 27.4298 120.224 25.2947 120.224 21.4088V21.4063ZM130.809 22.2453C130.809 19.54 132.218 17.7867 134.848 16.797C136.755 16.1113 139.191 15.8827 142.469 15.8827H144.185V15.1593C144.185 13.3306 143.346 11.2332 139.842 11.2332C137.099 11.2332 135.765 12.7202 135.65 14.471H131.266C131.533 10.6605 134.924 7.87981 139.917 7.87981C145.594 7.87981 148.644 10.8137 148.644 15.6918V27.4273H144.338V24.9506C143.499 26.1312 141.633 28 137.441 28C133.248 28 130.809 25.7519 130.809 22.2453ZM138.815 24.722C142.243 24.722 144.341 22.6648 144.341 19.5777V18.8166H142.703C140.035 18.8166 138.32 18.9698 137.177 19.427C135.881 19.9595 135.424 20.8361 135.424 22.0569C135.424 23.5037 136.491 24.7245 138.815 24.7245V24.722Z"
                fill="currentColor"
              />
            </g>
            <defs>
              <clipPath id="clip0_1924_2917">
                <rect width="148.641" height="28" fill="currentColor" />
              </clipPath>
            </defs>
          </svg>
        </div>

        {/* Loading bar */}
        <div className="w-64 h-1 bg-monta-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-monta-black rounded-full animate-[loading_1.5s_ease-in-out_infinite]"></div>
        </div>

        {/* Custom keyframe animation */}
        <style>{`
          @keyframes loading {
            0% {
              width: 0%;
              margin-left: 0%;
            }
            50% {
              width: 40%;
              margin-left: 30%;
            }
            100% {
              width: 0%;
              margin-left: 100%;
            }
          }
        `}</style>
      </div>
    );
  }

  // Show error state if authentication failed
  if (authError) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-monta-gray-50">
        <div className="text-center p-8 max-w-md">
          <svg
            className="w-16 h-16 text-monta-red mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-monta-gray-800 mb-2">
            Connection Error
          </h2>
          <p className="text-monta-gray-600 mb-6">
            Unable to connect to the service. Please check your internet
            connection and try again.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                clearError();
                void initialize();
              }}
              className="w-full px-6 py-3 bg-monta-black text-white rounded-full hover:bg-monta-black-600 transition-colors font-medium"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full px-6 py-3 bg-white text-monta-gray-700 border border-monta-gray-300 rounded-full hover:bg-monta-gray-50 transition-colors font-medium"
            >
              Reload Page
            </button>
          </div>
          {process.env.NODE_ENV === 'development' && (
            <details className="mt-6 text-left">
              <summary className="text-sm text-monta-gray-500 cursor-pointer hover:text-monta-gray-700">
                Error Details
              </summary>
              <pre className="mt-2 p-3 bg-monta-gray-100 rounded text-xs text-monta-gray-700 overflow-auto">
                {authError.message}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {children}
      <ChargeSessionDetailPanel />
    </>
  );
}

function App() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <PostHogProvider>
        <QueryClientProvider client={queryClient}>
          <I18nextProvider i18n={i18n}>
            {/* React Router v7: startTransition + relative splat paths are now
                the default (previously opted into via future flags) */}
            <BrowserRouter>
              <Routes>
                {/* Documentation route */}
                <Route path="/docs" element={<DocsPage />} />
                {/* Readme route */}
                <Route path="/readme" element={<ReadmePage />} />
                {/* Stress test route - overlays the map */}
                <Route
                  path="/test"
                  element={
                    <AppInitializer>
                      <div className="relative">
                        <ResponsiveLayout />
                        <StressTest />
                      </div>
                    </AppInitializer>
                  }
                />
                {/* Main map route */}
                <Route
                  path="*"
                  element={
                    <AppInitializer>
                      <ResponsiveLayout />
                    </AppInitializer>
                  }
                />
              </Routes>
            </BrowserRouter>
            <ReactQueryDevtools initialIsOpen={false} />
          </I18nextProvider>
        </QueryClientProvider>
      </PostHogProvider>
    </ErrorBoundary>
  );
}

export default App;
